import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Selection of the price table (R19). Either a set of ids, or "all
 * products matching the filters" with an exclusion list for rows the
 * admin deselected afterwards. The filtered mode remembers the total the
 * API reported and the filter key it was taken under.
 */
export type RowSelection =
  | { mode: 'ids'; ids: ReadonlySet<string> }
  | {
      mode: 'filtered';
      excludedIds: ReadonlySet<string>;
      total: number;
      filterKey: string;
    };

export const EMPTY_SELECTION: RowSelection = {
  mode: 'ids',
  ids: new Set<string>(),
};

export interface UseRowSelectionOptions {
  /**
   * Identifies the filters and search; a change empties the selection
   * (the rows it referred to are no longer the rows on screen). Paging
   * and view settings are not part of it.
   */
  filterKey: string;
  /** Called when a filter change emptied a non-empty selection. */
  onReset?: () => void;
}

export interface RowSelectionApi {
  selection: RowSelection;
  /** Selected rows; in filtered mode the total minus the exclusions. */
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Selects every given row, or deselects all of them when all are selected. */
  togglePage: (ids: readonly string[]) => void;
  selectAllFiltered: (total: number) => void;
  clear: () => void;
}

export function selectionCount(selection: RowSelection): number {
  return selection.mode === 'ids'
    ? selection.ids.size
    : Math.max(0, selection.total - selection.excludedIds.size);
}

export function isRowSelected(selection: RowSelection, id: string): boolean {
  return selection.mode === 'ids'
    ? selection.ids.has(id)
    : !selection.excludedIds.has(id);
}

/** A filtered selection with nothing left in it falls back to an empty set. */
function normalize(selection: RowSelection): RowSelection {
  return selection.mode === 'filtered' && selectionCount(selection) === 0
    ? EMPTY_SELECTION
    : selection;
}

export function useRowSelection({
  filterKey,
  onReset,
}: UseRowSelectionOptions): RowSelectionApi {
  const [selection, setSelection] = useState<RowSelection>(EMPTY_SELECTION);

  // Refs for the effect below: the reset must read the selection of the
  // render the key changed in, without re-running on every selection.
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;
  const countRef = useRef(0);
  countRef.current = selectionCount(selection);
  const previousKey = useRef(filterKey);

  useEffect(() => {
    if (previousKey.current === filterKey) return;
    previousKey.current = filterKey;
    if (countRef.current === 0) return;
    setSelection(EMPTY_SELECTION);
    onResetRef.current?.();
  }, [filterKey]);

  const toggle = useCallback((id: string) => {
    setSelection((current) => {
      if (current.mode === 'ids') {
        const ids = new Set(current.ids);
        if (ids.has(id)) ids.delete(id);
        else ids.add(id);
        return { mode: 'ids', ids };
      }
      const excludedIds = new Set(current.excludedIds);
      if (excludedIds.has(id)) excludedIds.delete(id);
      else excludedIds.add(id);
      return normalize({ ...current, excludedIds });
    });
  }, []);

  const togglePage = useCallback((pageIds: readonly string[]) => {
    setSelection((current) => {
      const allSelected =
        pageIds.length > 0 && pageIds.every((id) => isRowSelected(current, id));
      if (current.mode === 'ids') {
        const ids = new Set(current.ids);
        for (const id of pageIds) {
          if (allSelected) ids.delete(id);
          else ids.add(id);
        }
        return { mode: 'ids', ids };
      }
      const excludedIds = new Set(current.excludedIds);
      for (const id of pageIds) {
        if (allSelected) excludedIds.add(id);
        else excludedIds.delete(id);
      }
      return normalize({ ...current, excludedIds });
    });
  }, []);

  const selectAllFiltered = useCallback(
    (total: number) => {
      setSelection(
        normalize({
          mode: 'filtered',
          excludedIds: new Set<string>(),
          total,
          filterKey,
        })
      );
    },
    [filterKey]
  );

  const clear = useCallback(() => setSelection(EMPTY_SELECTION), []);

  const isSelected = useCallback(
    (id: string) => isRowSelected(selection, id),
    [selection]
  );

  return useMemo(
    () => ({
      selection,
      count: selectionCount(selection),
      isSelected,
      toggle,
      togglePage,
      selectAllFiltered,
      clear,
    }),
    [selection, isSelected, toggle, togglePage, selectAllFiltered, clear]
  );
}
